import React, { InputHTMLAttributes, forwardRef, useState, useRef, useEffect, useId } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  prefix?: string;
  suffix?: string;
  tooltip?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, prefix, suffix, tooltip, className = '', ...props }, ref) => {
    const generatedId = useId();
    const inputId = props.id ?? generatedId;
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [offsetX, setOffsetX] = useState(0);
    const iconRef = useRef<HTMLSpanElement>(null);
    const tooltipRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
      if (!tooltipVisible || !iconRef.current || !tooltipRef.current) return;

      const iconRect = iconRef.current.getBoundingClientRect();
      const tipRect = tooltipRef.current.getBoundingClientRect();
      const margin = 8;

      // Базовое смещение: центрируем тултип по иконке
      let shift = 0;
      const tipLeft = iconRect.left + iconRect.width / 2 - tipRect.width / 2;
      const tipRight = tipLeft + tipRect.width;

      if (tipLeft < margin) {
        shift = margin - tipLeft;
      } else if (tipRight > window.innerWidth - margin) {
        shift = window.innerWidth - margin - tipRight;
      }

      setOffsetX(shift);
    }, [tooltipVisible]);

    const show = () => { setOffsetX(0); setTooltipVisible(true); };
    const hide = () => setTooltipVisible(false);

    return (
      <div className="input-group">
        {label && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label className="input-label" htmlFor={inputId}>{label}</label>
            {tooltip && (
              <span
                ref={iconRef}
                style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
                onMouseEnter={show}
                onMouseLeave={hide}
                onTouchStart={e => { e.preventDefault(); tooltipVisible ? hide() : show(); }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ cursor: 'help', flexShrink: 0, opacity: 0.5 }}
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 16v-4m0-4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                {tooltipVisible && (
                  <span
                    ref={tooltipRef}
                    style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 6px)',
                      left: '50%',
                      transform: `translateX(calc(-50% + ${offsetX}px))`,
                      background: 'var(--surface-3, #2a2a2a)',
                      color: 'var(--text)',
                      fontSize: '12px',
                      lineHeight: '1.4',
                      padding: '7px 10px',
                      borderRadius: '7px',
                      whiteSpace: 'normal',
                      width: 'max-content',
                      maxWidth: 'min(260px, calc(100vw - 32px))',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
                      border: '1px solid var(--border)',
                      zIndex: 100,
                      pointerEvents: 'none',
                    }}>
                    {tooltip}
                    <span style={{
                      position: 'absolute',
                      top: '100%',
                      left: `calc(50% - ${offsetX}px)`,
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '5px solid transparent',
                      borderRight: '5px solid transparent',
                      borderTop: '5px solid var(--border)',
                    }} />
                  </span>
                )}
              </span>
            )}
          </div>
        )}
        <div className={`input-wrapper ${error ? 'input-error' : ''}`}>
          {prefix && <span className="input-prefix">{prefix}</span>}
          <input ref={ref} id={inputId} className={`input-field ${className}`} {...props} />
          {suffix && <span className="input-suffix">{suffix}</span>}
        </div>
        {hint && !error && <span className="input-hint">{hint}</span>}
        {error && <span className="input-error-msg">{error}</span>}
      </div>
    );
  }
);

Input.displayName = 'Input';
export default Input;
